import { FeatureFlags } from '@fabric-ui/components';
import { useAvContext } from '../../../context/AvContextProvider';
import getNewRequestParams from '../../../utils/getNewRequestParams';
import { barChartMaxSize, chartsDefaultSize, tableDefaultSize, widgetTypeObject } from '../constants';
import { getInitialWidgetData, getTrimmedDimsForWidgetType, getWidgetLimit, trendDefaultRequestParams } from '../CustomDashboardsUtils';
import { cleanRequests } from '../CustomDashboardsUtils/utils';
import {
  AllTypeWidgets,
  BarSubType,
  CustomDashboardDto,
  DwQueryRequest,
  LineSubType,
  PieSubType,
  TableSubType,
  TableTypeWidget,
  TileSubType,
  TileTypeWidget,
  TileWidgetVisualization,
  WidgetCategory,
} from '../types';
import { interactiveQueryMaxRowsSize } from '../types/QueryObject.types';
import { FieldType } from '../types/types';
import { useGetDisplayName } from './useGetDisplayName';

const updateWidgetFieldsWhenChangingWidgetType = ({
  currentWidget,
  defaultNewWidget,
  featureFlags,
}: {
  currentWidget: AllTypeWidgets;
  defaultNewWidget: AllTypeWidgets;
  featureFlags: Record<string, boolean>;
}): AllTypeWidgets => {
  const {
    select: { dims, metrics },
  } = currentWidget.requests[0];

  const tempWidgetForDimsLimit = { ...defaultNewWidget, requests: currentWidget.requests };
  const dimsLimit = getWidgetLimit(tempWidgetForDimsLimit, FieldType.Dimensions);

  const trimmedDims = getTrimmedDimsForWidgetType({
    dims,
    dimsLimit,
    category: defaultNewWidget.category,
  });

  const tempWidgetForMetricsLimit = {
    ...defaultNewWidget,
    requests: [{ ...currentWidget.requests[0], select: { ...currentWidget.requests[0].select, dims: trimmedDims } }],
  };
  const metricsLimit = getWidgetLimit(tempWidgetForMetricsLimit, FieldType.Measurements);

  const keptDimNames = new Set(trimmedDims.map(d => d.name));
  const dimsToRemove = dims.filter(d => !keptDimNames.has(d.name)).map(d => d.name);
  const metricsToRemove = metricsLimit !== undefined && metrics.length > metricsLimit ? metrics.slice(metricsLimit).map(m => m.name) : [];

  const fieldsToRemove = [
    { fields: dimsToRemove, fieldCategory: FieldType.Dimensions as const },
    { fields: metricsToRemove, fieldCategory: FieldType.Measurements as const },
  ];

  return fieldsToRemove.reduce<AllTypeWidgets>((accWidget, { fields, fieldCategory }) => {
    if (fields.length > 0) {
      const { newSelect, newGroupBy, newSortBy, newDistinct, newDrillDownHierarchy, newTop, newSortingCol } = getNewRequestParams({
        widget: accWidget,
        fields,
        fieldCategory,
        isHistoricalAnalyticsFeatureFlag: featureFlags[FeatureFlags.HistoricalAnalytics],
      });
      return {
        ...accWidget,
        requests: [
          {
            ...accWidget.requests[0],
            select: newSelect,
            groupBy: newGroupBy,
            sorting: newSortBy,
            distinct: newDistinct,
            top: newTop,
            ...(newSortingCol ? { sortingCol: newSortingCol } : {}),
          },
        ],
        drillDownHierarchy: newDrillDownHierarchy,
      } as AllTypeWidgets;
    }
    return accWidget;
  }, currentWidget);
};

const useInitWidget = ({ projectionName, initialDashboard }: { projectionName: string; initialDashboard: CustomDashboardDto }) => {
  const { featureFlags, measurements } = useAvContext();
  const getDisplayName = useGetDisplayName(projectionName);

  return ({
    selectedWidgetType,
    selectedWidgetCategory,
    currentWidget,
  }: {
    selectedWidgetType: PieSubType | TableSubType | BarSubType | LineSubType | TileSubType;
    selectedWidgetCategory: WidgetCategory;
    currentWidget: AllTypeWidgets;
  }) => {
    const defaultNewWidget = widgetTypeObject[selectedWidgetCategory][selectedWidgetType].default;

    const widgetToInitialize = updateWidgetFieldsWhenChangingWidgetType({ currentWidget, defaultNewWidget, featureFlags });

    const initializedRequests = cleanRequests(widgetToInitialize);
    const definition = { ...defaultNewWidget.definition, title: { title: widgetToInitialize.definition.title.title } };

    if (selectedWidgetType === TableSubType.PivotTable) {
      const request = initializedRequests[0];
      const newSorting = widgetToInitialize.id
        ? getInitialWidgetData(widgetToInitialize.id, initialDashboard.widgets)?.requests?.[0]?.sorting
        : undefined;

      const top = request.top && request.top.size === tableDefaultSize ? { ...request.top, size: chartsDefaultSize } : request.top;

      return {
        definition: { ...definition, custom: { ...definition.custom, showColTotals: false } },
        requests: [
          {
            ...request,
            select: { ...request.select, dims: [] },
            sortingCol: [],
            sorting: newSorting ?? [],
            top: {
              ...top,
              size: top?.size || chartsDefaultSize,
            },
          },
        ],
      };
    }
    if (
      selectedWidgetType === TileSubType.Tile &&
      (widgetToInitialize as TileTypeWidget).definition.custom?.visualization === TileWidgetVisualization.Progress
    ) {
      return {
        ...defaultNewWidget,
        definition,
        requests: initializedRequests.map(r => ({
          ...r,
          metricsFilterThreshold: widgetToInitialize.type === TileSubType.Tile ? undefined : 0,
          select: {
            ...r.select,
            metrics: r.select.metrics.map(m => {
              const { name, alias } = m;
              if (
                currentWidget.type === TileSubType.Tile &&
                (currentWidget as TileTypeWidget).definition.custom.visualization === TileWidgetVisualization.Progress
              ) {
                return { name, alias, conf: { percentage: { breakdown: true } } };
              }

              return { name, alias };
            }),
          },
          ...(r.top ? { top: { ...r.top, offset: 0 } } : r.top),
        })),
      };
    }

    if (selectedWidgetCategory === WidgetCategory.Tile) {
      const isEmptyTitle = widgetToInitialize.definition.title.title === '';
      const metric = widgetToInitialize.requests[0].select.metrics?.[0] || '';
      const customDefinition = { ...defaultNewWidget.definition, title: { title: getDisplayName(metric.alias || metric.name) } };

      if (selectedWidgetType === TileSubType.Tile) {
        return {
          ...defaultNewWidget,
          definition: isEmptyTitle ? customDefinition : definition,
          requests: initializedRequests,
        };
      }
      if (selectedWidgetType === TileSubType.Trend) {
        const historicSelectedMetrics = initializedRequests[0].select.metrics.filter(
          selectedMetric => measurements.asObject[selectedMetric.name]?.timeExpression
        );
        return {
          ...defaultNewWidget,
          definition: isEmptyTitle ? customDefinition : definition,
          requests: [
            {
              ...initializedRequests[0],
              ...trendDefaultRequestParams({
                ...initializedRequests[0],
                select: { ...initializedRequests[0].select, metrics: historicSelectedMetrics },
              }),
            },
          ],
        };
      }
    }

    if (selectedWidgetType === TableSubType.Table) {
      return {
        ...defaultNewWidget,
        definition: {
          ...definition,
          custom: { ...definition.custom, pageSize: tableDefaultSize },
        },
        requests: initializedRequests.map(r => ({
          ...r,
          top: r.top && r.top.size === tableDefaultSize ? { ...r.top, size: undefined } : r.top,
        })),
      } as TableTypeWidget;
    }

    const isBarChart = selectedWidgetCategory === WidgetCategory.Bar;
    const defaultChartTopSize = isBarChart ? barChartMaxSize : tableDefaultSize;
    const topSizeCap = isBarChart ? barChartMaxSize : interactiveQueryMaxRowsSize;
    return {
      ...defaultNewWidget,
      requests: initializedRequests.map((r: DwQueryRequest) => ({
        ...r,
        top: {
          ...r.top,
          size: r.top?.size != null ? Math.min(r.top.size, topSizeCap) : defaultChartTopSize,
        },
      })),
      definition,
    }; // Default return, using adjusted requests and definition
  };
};

export default useInitWidget;
